// src/pages/attendance/components/MonthlyTable.jsx
import React, { useMemo } from "react";
import { BarChart3, Clock, TrendingUp } from "lucide-react";
import { Mo3tiCard, Mo3tiProgressBar } from "./mo3tiComponents";
import { getStatusColor, getStatusText, formatTimeForDisplay, calculateWorkingHours } from "../utils/attendanceHelpers";
import { getAttendanceStyles } from "../styles/attendanceStyles";

const MonthlyTable = ({ monthlyAttendance, timeEntries, isLoadingTimeEntries, t, currentTheme, isDarkMode, language, attendanceStats }) => {
  const styles = getAttendanceStyles(currentTheme, isDarkMode, language);
  const c = currentTheme.colors;
  const f = currentTheme.fonts;

  // performance metrics (moved here from main page)
  const performanceMetrics = useMemo(() => {
    if (!attendanceStats || attendanceStats.length < 4) {
      return [
        { label: t.performance.attendanceRate, current: 0, target: 95, color: c.success },
        { label: t.performance.punctuality,    current: 0, target: 90, color: c.warning },
        { label: t.performance.avgHoursPerDay, current: 0, target: 8,  color: c.info    },
        { label: t.performance.overtimeHours,  current: 0, target: 10, color: c.primary },
      ];
    }
    const present = parseInt(attendanceStats[0]?.value) || 0;
    const absent  = parseInt(attendanceStats[1]?.value) || 0;
    const late    = parseInt(attendanceStats[2]?.value) || 0;
    const totalHours = parseFloat(String(attendanceStats[3]?.value || "0").split(" ")[0]) || 0;
    const total   = present + absent + late;
    const attended = present + late;
    return [
      { label: t.performance.attendanceRate, current: parseFloat((total > 0 ? (attended / total) * 100 : 0).toFixed(1)), target: 95, color: c.success },
      { label: t.performance.punctuality,    current: parseFloat((attended > 0 ? (present / attended) * 100 : 0).toFixed(1)), target: 90, color: c.warning },
      { label: t.performance.avgHoursPerDay, current: parseFloat((attended > 0 ? totalHours / attended : 0).toFixed(1)),      target: 8,  color: c.info    },
      { label: t.performance.overtimeHours,  current: parseFloat(Math.max(0, totalHours - attended * 8).toFixed(1)),           target: 10, color: c.primary },
    ];
  }, [attendanceStats, c, t]);

  const isRateLabel = (label) =>
    label.includes(language === "ar" ? "معدل" : "Rate") ||
    label.includes(language === "ar" ? "الالتزام" : "Punctuality");

  return (
    <>
      {/* Performance metrics */}
      <Mo3tiCard title={t.performance.title} icon={TrendingUp} theme={currentTheme}>
        <div style={styles.metricsGrid}>
          {performanceMetrics.map((m, i) => (
            <div key={i} style={styles.metricCard}>
              <div style={styles.metricHeader}>
                <h4 style={styles.metricTitle}>{m.label}</h4>
                <span style={styles.metricValue(m.color)}>{m.current < 10 ? m.current.toFixed(1) : m.current}{isRateLabel(m.label) ? "%" : ""}</span>
              </div>
              <Mo3tiProgressBar percentage={(m.current / m.target) * 100} color={m.color} theme={currentTheme} showLabel={false} />
              <div style={styles.metricFooter}>
                <span>{t.performance.current} {m.current}{isRateLabel(m.label) ? "%" : ""}</span>
                <span>{t.performance.target} {m.target}{isRateLabel(m.label) ? "%" : ""}</span>
              </div>
            </div>
          ))}
        </div>
      </Mo3tiCard>

      {/* Monthly table */}
      <Mo3tiCard title={t.monthly.title} icon={BarChart3} theme={currentTheme}>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>{[t.monthly.month, t.monthly.presentDays, t.monthly.absentDays, t.monthly.lateDays, t.monthly.totalDays, t.monthly.attendanceRate, t.monthly.performance].map((h, i) => (
                <th key={i} style={styles.tableHeader}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {monthlyAttendance.map((month, i) => (
                <tr key={i} style={styles.tableRow}>
                  <td style={styles.tableCell}><div style={{ fontWeight: f.weights.semibold, fontFamily: "'Cairo', sans-serif" }}>{month.month}</div></td>
                  {["present", "absent", "late"].map((key) => (
                    <td key={key} style={styles.tableCell}><div style={styles.statusIndicator(key, c)}>{month[key]}</div></td>
                  ))}
                  <td style={styles.tableCell}>{month.totalDays}</td>
                  <td style={styles.tableCell}>
                    <span style={{ color: month.rate >= 90 ? c.success : month.rate >= 80 ? c.warning : c.danger, fontWeight: f.weights.semibold, fontFamily: "'Cairo', sans-serif" }}>{month.rate}%</span>
                  </td>
                  <td style={styles.tableCell}>
                    <div style={{ width: "120px" }}>
                      <Mo3tiProgressBar percentage={month.rate} color={month.rate >= 90 ? c.success : month.rate >= 80 ? c.warning : c.danger} theme={currentTheme} size="sm" showLabel={false} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Mo3tiCard>

      {/* Recent records */}
      <Mo3tiCard title={t.recent.title} icon={Clock} theme={currentTheme}>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>{[t.recent.date, t.recent.checkIn, t.recent.checkOut, t.recent.totalHours, t.recent.status].map((h, i) => (
                <th key={i} style={styles.tableHeader}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {timeEntries.map((entry, i) => (
                <tr key={i} style={styles.tableRow}>
                  <td style={styles.tableCell}><div style={{ fontWeight: f.weights.semibold, fontFamily: "'Cairo', sans-serif" }}>{new Date(entry.date).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")}</div></td>
                  <td style={styles.tableCell}><span style={{ fontWeight: f.weights.semibold, fontFamily: "'Cairo', sans-serif" }}>{formatTimeForDisplay(entry.checkIn, language)}</span></td>
                  <td style={styles.tableCell}><span style={{ fontWeight: f.weights.semibold, fontFamily: "'Cairo', sans-serif" }}>{formatTimeForDisplay(entry.checkOut, language)}</span></td>
                  <td style={styles.tableCell}><span style={{ fontWeight: f.weights.bold, color: c.primary, fontFamily: "'Cairo', sans-serif" }}>{entry.totalHours || calculateWorkingHours(entry.checkIn, entry.checkOut)} {t.units.hours}</span></td>
                  <td style={styles.tableCell}>
                    <div style={
                      entry.status === "absent" || entry.status === "leave"
                        ? styles.statusIndicator("absent", c)
                        : styles.statusIndicator(entry.status === "Complete" ? "present" : entry.status, c)
                    }>
                      {getStatusText(entry.status, t)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Mo3tiCard>
    </>
  );
};

export default MonthlyTable;